import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CreateQueryResult } from '@tanstack/angular-query-experimental';
import { PrimeNGConfig } from 'primeng/api';
import { AppInitializerService, UtmTrackerService } from '@/common/services';
import { TranslationService } from '@/modules/i18n';
import { User } from '@/pages/auth/models';
import { AuthService } from '@/pages/auth/services/auth.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  protected readonly environment = environment;

  queryProfile: CreateQueryResult<User, HttpErrorResponse> = this._authService.getProfile();

  constructor(
    @Inject(PLATFORM_ID) private _platformId: object,
    private _translationService: TranslationService,
    private _primengConfig: PrimeNGConfig,
    private _authService: AuthService,
    private _appInitializerService: AppInitializerService,
    private _utmTracker: UtmTrackerService,
  ) {
    // Si el usuario ya está logueado, bloquear el splash de inmediato hasta
    // saber su idioma preferido. Esto evita que se muestre contenido con el
    // idioma por defecto (español) antes de que se cargue el idioma del usuario.
    if (this._authService.isLogged()) {
      this._appInitializerService.markUserTranslationsLoading();
    }

    effect(() => {
      if (!this._authService.isLogged()) {
        // Usuario no logueado (o acaba de hacer logout por 401/token expirado).
        // Desbloquear el splash por si se llamó markUserTranslationsLoading() antes.
        this._appInitializerService.markUserTranslationsLoaded();
        return;
      }

      // Si el perfil falla, desbloquear el splash con el idioma por defecto
      if (this.queryProfile.isError()) {
        this._appInitializerService.markUserTranslationsLoaded();
        return;
      }

      if (this.queryProfile.data()) {
        this._authService.updateUser(this.queryProfile.data());
        const userLocale = this.queryProfile.data()!.locale || 'es';

        this._translationService.setLanguage(userLocale).subscribe({
          next: () => {
            this._appInitializerService.markUserTranslationsLoaded();
          },
          error: () => {
            this._appInitializerService.markUserTranslationsLoaded();
          },
        });
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this._primengConfig.ripple = false;

    // Inicializar tracking de UTM parameters
    if (isPlatformBrowser(this._platformId)) {
      this._utmTracker.captureUtmFromUrl();
    }
  }
}
